import React, { forwardRef, useMemo, useCallback } from 'react';
import { FieldRenderProps, useField } from 'react-final-form';
import validations from 'validations';
import classNames from 'classnames';
import { getClass } from './utils';
import { useTranslation } from 'react-i18next';
import Collapse from 'components/Collapse';
import {Input} from "ncoded-component-library"
import { ExtractPropTypes } from 'types';

import './InputField.styles.scss';

type InputProps = ExtractPropTypes<typeof Input>;
export type InputFieldProps = InputProps &
  FieldRenderProps<string, HTMLElement>;

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      label,
      input,
      meta: { touched, error, ...metarest },
      required,
      onChangeHandle,
      className,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation();

    useField(input.name, {
      ...(required
        ? { validate: validations.required(t('required-message')) }
        : {}),
    });

    const hasError = useMemo(() => touched && error, [touched, error]);

    const classes = classNames(
      'input-field',
      'fm-form-field',
      {
        'input-field--error': hasError,
      },
      className,
    );

    const handleOnChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value =
          onChangeHandle?.(event.target.value) || event.target.value;
        input.onChange(value);
      },
      [input, onChangeHandle],
    );

    return (
      <div className={classes}>
        {label && <label className={classNames({ required })}>{label}</label>}
        <Input
          {...input}
          {...rest}
          onChange={handleOnChange}
          ref={ref}
          className={getClass`__input`}
        />
        {hasError && (  
          <Collapse active>
            <small className="field-error">{error}</small>
          </Collapse>
        )}
      </div>
    );
  },
);

export default InputField;
